/**
 * v0.4 使い勝手フィードバック② 増分① (D-U22): 承認パターン選択に関わる純粋ロジック。
 * `ApprovalsPage.tsx` の `WorkflowSettingsPanel` に直接書いていた
 * 「対象パターン → workflow_type の既定パターン → 1」という3段フォールバックと、
 * 追加パターンを含む workflow_id → workflow_type のラベル解決マップ構築を抽出する
 * (test-runner指摘: `lib/shift-board-filters.ts` と同じ理由 — ページ内ロジックのまま
 * 放置すると変更が無テストで壊れる)。
 */

export type WorkflowSetting = { workflow_id: string; workflow_type: string; label: string; max_levels: number; is_active: boolean };
export type WorkflowPatternRow = {
  workflow_id: string; workflow_type: string; code: string; label: string; max_levels: number; is_active: boolean;
};

/** workflow_type ごとの全パターン一覧 (`patternsByType`) から、追加パターンを含む
 *  workflow_id → workflow_type の完全なマップを構築する。
 *  ⚠️ `GET /approvals/workflows` (code='default' のみ返す) だけでは追加パターンの workflow_id が
 *  解決できず、override/recipient 一覧のラベルが生IDのまま表示される (詳細設計 §2-2 の既知ギャップ)。 */
export const buildWorkflowTypeByWorkflowId = (
  patternsByType: Map<string, WorkflowPatternRow[]>,
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const list of patternsByType.values()) {
    for (const p of list) map.set(p.workflow_id, p.workflow_type);
  }
  return map;
};

/** 承認者上書き/受信者設定フォームの「段階」選択肢を決めるための最大段階数を解決する。
 *  優先順位: ①選択中パターン (workflowId で patterns から特定) の max_levels
 *          → ②workflow_type の既定パターン (workflows、code='default') の max_levels
 *          → ③1 (どちらも見つからない場合のフォールバック)。
 *  ⚠️ `workflows` (`GET /approvals/workflows`) は code='default' のみを返すため、
 *  追加パターンが選択されている場合はその段数と食い違う。①を優先することで、
 *  段階の選択肢が「選択中パターンの実際の段数」を正しく反映する (詳細設計 §2-2)。 */
export const resolveEffectiveMaxLevels = (
  patterns: readonly WorkflowPatternRow[],
  selectedWorkflowId: string,
  workflows: readonly WorkflowSetting[],
  workflowType: string,
): number => (
  patterns.find((p) => p.workflow_id === selectedWorkflowId)?.max_levels
  ?? workflows.find((w) => w.workflow_type === workflowType)?.max_levels
  ?? 1
);

/** 使い勝手フィードバック④ (D-U18): max_levels=0 (ゼロ段階承認) は「最大◯段階」ではなく通知のみと
 *  明示する。ApprovalsPage.tsx (パターン一覧・段階数セレクト) と RequestTypesSettingsPage.tsx
 *  (承認パターン選択) の両方が使う (§8-55: 同じ判定を2箇所に書かない)。 */
export const formatMaxLevels = (maxLevels: number): string =>
  maxLevels === 0 ? '承認なし (通知のみ)' : `最大${maxLevels}段階`;

/** workflow_type 内の「既定パターン (code='default')」の workflow_id を解決する。
 *  見つからない場合 (patterns が空、または default 行がまだ読み込まれていない) は undefined を返す。 */
export const resolveDefaultPatternWorkflowId = (
  patterns: readonly WorkflowPatternRow[],
): string | undefined => patterns.find((p) => p.code === 'default')?.workflow_id;
